// =============================================================
// EmptyState — icon + title + optional hint + optional action button. The
// "no data" surface for tables/cards. Mirrors the prototype's .ad-empty look
// (centred stroke-icon, muted copy) but generalised + typed. Tokens only.
// =============================================================

import type { ReactNode } from "react";
import { Btn, Icon, ICONS } from "./primitives.js";

export interface EmptyStateProps {
  /** Stroke-icon path (24×24 viewBox). Defaults to the inbox glyph. */
  icon?: string;
  /** Primary line — what's missing. */
  title: string;
  /** Optional secondary line — why / what to do next. */
  hint?: ReactNode;
  /** Optional call-to-action button. */
  action?: { label: string; onClick: () => void; disabled?: boolean };
  /** Tightens vertical padding for inline (in-card) use. */
  compact?: boolean;
}

/** Centred empty-state block for "no data" / "nothing matches" surfaces. */
export function EmptyState({ icon = ICONS.inbox, title, hint, action, compact = false }: EmptyStateProps) {
  return (
    <div className={`empty-state${compact ? " compact" : ""}`} role="status">
      <div className="empty-ic" aria-hidden="true">
        <Icon d={icon} size={compact ? 28 : 40} sw={1.6} />
      </div>
      <p className="empty-title">{title}</p>
      {hint != null && <p className="empty-hint">{hint}</p>}
      {action && (
        <Btn variant="secondary" size="sm" onClick={action.onClick} disabled={action.disabled} className="empty-action">
          {action.label}
        </Btn>
      )}
    </div>
  );
}
